export type GuideHtmlClassReferenceGroup = {
  title: string;
  items: Array<{
    name: string;
    description: string;
  }>;
};

export const GUIDE_HTML_STARTER_TEMPLATE = `<div class="guide-doc">
  <section class="guide-section guide-hero">
    <div class="guide-eyebrow">TV Labs Documentation</div>
    <h1>TV Remote Testing Portal</h1>
    <p class="guide-lead">
      Add a short overview that explains what this topic covers and what the reader should expect.
    </p>
    <div class="guide-chip-row">
      <span class="guide-chip">Playbook</span>
      <span class="guide-chip">QA Guide</span>
      <span class="guide-chip">Support</span>
    </div>
  </section>

  <section class="guide-grid guide-grid--3">
    <article class="guide-card">
      <span class="guide-stat-label">Purpose</span>
      <h3>What this section solves</h3>
      <p>Summarize the main job to be done for the user.</p>
    </article>
    <article class="guide-card guide-card--soft">
      <span class="guide-stat-label">Key capability</span>
      <h3>Important workflow</h3>
      <p>Highlight the core action or flow that matters most.</p>
    </article>
    <article class="guide-card">
      <span class="guide-stat-label">Primary users</span>
      <h3>Who should read this</h3>
      <p>Call out the teams or personas this content is for.</p>
    </article>
  </section>

  <section class="guide-section">
    <h2>Introduction</h2>
    <p>
      Use regular headings, paragraphs, lists, tables, images, and videos. The visual design comes
      from the built-in guide classes, so you do not need inline styles.
    </p>
    <div class="guide-callout">
      <strong>Tip:</strong> Keep the structure semantic. Avoid full HTML documents, style tags, and
      inline CSS in this editor.
    </div>
  </section>

  <section class="guide-split">
    <article class="guide-section">
      <h2>Step-by-step flow</h2>
      <ol>
        <li>Explain the first action the user should take.</li>
        <li>Describe what they should validate on screen.</li>
        <li>Call out the expected result or next step.</li>
      </ol>
    </article>
    <article class="guide-section">
      <h2>Checklist</h2>
      <ul>
        <li>Pre-requisites</li>
        <li>Common checks</li>
        <li>Escalation notes</li>
      </ul>
    </article>
  </section>

  <section class="guide-section">
    <h2>Media block</h2>
    <figure class="guide-media-frame">
      <div class="guide-placeholder">
        Replace this placeholder with the image or video snippet copied from the media library.
        <small>Paste the copied embed snippet here when assets are uploaded.</small>
      </div>
      <figcaption class="guide-caption">
        Add a caption to explain what the reader should notice in the media.
      </figcaption>
    </figure>
  </section>

  <section class="guide-section">
    <h2>Reference table</h2>
    <div class="guide-table-wrap">
      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Example row</td>
            <td>Use tables for structured reference information.</td>
          </tr>
          <tr>
            <td>Another item</td>
            <td>Keep rows concise and easy to scan.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</div>`;

export const GUIDE_HTML_CLASS_REFERENCE_GROUPS: GuideHtmlClassReferenceGroup[] =
  [
    {
      title: 'Page structure',
      items: [
        {
          name: 'guide-doc',
          description: 'Outer wrapper for a polished multi-section guide page.',
        },
        {
          name: 'guide-section',
          description: 'Rounded content panel with the standard user guide card styling.',
        },
        {
          name: 'guide-split',
          description: 'Two-column section layout that stacks automatically on smaller screens.',
        },
      ],
    },
    {
      title: 'Hero and labels',
      items: [
        {
          name: 'guide-hero',
          description: 'Large branded banner treatment for the opening section.',
        },
        {
          name: 'guide-eyebrow',
          description: 'Small uppercase label for section metadata or context.',
        },
        {
          name: 'guide-lead',
          description: 'Bigger introduction paragraph used beneath the main heading.',
        },
      ],
    },
    {
      title: 'Cards and layout',
      items: [
        {
          name: 'guide-grid guide-grid--2',
          description: 'Responsive two-column card grid.',
        },
        {
          name: 'guide-grid guide-grid--3',
          description: 'Responsive three-column stat or capability layout.',
        },
        {
          name: 'guide-card',
          description: 'Default card shell for grouped content inside a section.',
        },
        {
          name: 'guide-card--soft',
          description: 'Subtle tinted card variant for secondary content.',
        },
        {
          name: 'guide-card--dark',
          description: 'Dark emphasis card for strong contrast callouts.',
        },
      ],
    },
    {
      title: 'Content accents',
      items: [
        {
          name: 'guide-chip-row and guide-chip',
          description: 'Pill-style labels for tags, audiences, or quick links.',
        },
        {
          name: 'guide-stat-label',
          description: 'Small uppercase kicker used above short content blocks.',
        },
        {
          name: 'guide-callout',
          description: 'Highlighted advisory block for tips, warnings, or notes.',
        },
        {
          name: 'guide-muted',
          description: 'Muted helper copy inside cards or captions.',
        },
      ],
    },
    {
      title: 'Media and tables',
      items: [
        {
          name: 'guide-media-frame',
          description: 'Framed image or video container with overflow handling.',
        },
        {
          name: 'guide-caption',
          description: 'Caption styling for figure descriptions.',
        },
        {
          name: 'guide-placeholder',
          description: 'Drop-in placeholder block before real media is uploaded.',
        },
        {
          name: 'guide-table-wrap',
          description: 'Horizontal scroll wrapper for wide tables.',
        },
      ],
    },
  ];
